import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Modal } from "../../../ui/components/Modal";
import { TextInput } from "../../../ui/components/Form";
import * as formStyles from "../../../ui/components/Form/FormInput.style.css";
import { MediaUploader } from "../../../ui/components/MediaUploader";
import type { MediaEntry } from "../../../ui/components/MediaUploader";
import { useAuth } from "../../lib/auth/hooks";
import { getAuthCookie } from "../../lib/auth/session-cookie";
import { useI18n } from "../../lib/i18n";
import * as localizationApi from "../../lib/api/localization";
import type { Language } from "../../lib/api/localization";
import { vars } from "../../../ui/styles/theme.css";

export interface LanguageFormModalProps {
  existing: Language | null;
  open: boolean;
  setOpen: (open: boolean) => void;
  onSaved?: (language: Language) => void;
}

export function LanguageFormModal({
  existing,
  open,
  setOpen,
  onSaved,
}: LanguageFormModalProps) {
  const { accessToken } = useAuth();
  const { t } = useI18n();
  const token = accessToken ?? getAuthCookie("access_token");
  const isEdit = existing != null;

  const [code, setCode] = React.useState("");
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [flagUrl, setFlagUrl] = React.useState("");
  const [enabled, setEnabled] = React.useState(true);
  const [isDefault, setIsDefault] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const isEnglish = (existing?.code ?? code).trim().toLowerCase() === "en";

  React.useEffect(() => {
    if (!open) return;
    setError(null);
    if (existing) {
      setCode(existing.code);
      setName(existing.name);
      setDescription(existing.description ?? "");
      setFlagUrl(existing.flagUrl ?? "");
      setEnabled(existing.enabled);
      setIsDefault(existing.isDefault);
    } else {
      setCode("");
      setName("");
      setDescription("");
      setFlagUrl("");
      setEnabled(true);
      setIsDefault(false);
    }
  }, [open, existing]);

  const flagItems: MediaEntry[] = React.useMemo(
    () => (flagUrl ? [{ id: existing?.id ?? "flag", url: flagUrl }] : []),
    [flagUrl, existing?.id],
  );

  const handleFlagApply = async (next: MediaEntry[]) => {
    const url = next[0]?.url ?? "";
    setFlagUrl(url);
    if (existing && token) {
      const updated = await localizationApi.updateLanguage(token, existing.id, {
        flagUrl: url || null,
      });
      onSaved?.(updated);
    }
  };

  const handleFlagRemove = async () => {
    setFlagUrl("");
    if (existing && token) {
      const updated = await localizationApi.updateLanguage(token, existing.id, {
        flagUrl: null,
      });
      onSaved?.(updated);
    }
  };

  const handleSave = async () => {
    if (!token) return;
    setSaving(true);
    setError(null);
    try {
      let saved: Language;
      if (isEdit && existing) {
        saved = await localizationApi.updateLanguage(token, existing.id, {
          name: name.trim(),
          description: description.trim() || null,
          flagUrl: flagUrl.trim() || null,
          enabled: isEnglish ? true : enabled,
          // Only send true when promoting — clearing default must promote another language.
          ...(isDefault && !existing.isDefault ? { isDefault: true } : {}),
        });
      } else {
        saved = await localizationApi.createLanguage(token, {
          code: code.trim().toLowerCase(),
          name: name.trim(),
          description: description.trim() || null,
          flagUrl: flagUrl.trim() || null,
          enabled,
          isDefault,
        });
      }
      onSaved?.(saved);
      setOpen(false);
    } catch (err: any) {
      setError(err?.message || t("pro.system.localization.error.saveLanguage"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen} minWidth="440px" maxWidth="520px">
      <Modal.Header>
        <Text level="h3" weight="bold">
          {isEdit
            ? t("pro.system.localization.form.editTitle")
            : t("pro.system.localization.form.createTitle")}
        </Text>
      </Modal.Header>
      <Modal.Body>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {error && <Callout variant="error">{error}</Callout>}

        {!isEdit && (
          <TextInput
            label={t("pro.system.localization.form.codeRequired")}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="en"
            disabled={saving}
          />
        )}
        {isEdit && (
          <div>
            <span className={formStyles.label}>
              {t("pro.system.localization.form.code")}
            </span>
            <div style={{ fontFamily: "monospace", fontSize: 14 }}>{code}</div>
          </div>
        )}

        <TextInput
          label={t("pro.system.localization.form.nameRequired")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("pro.system.localization.form.namePlaceholder")}
          disabled={saving}
        />

        <TextInput
          label={t("pro.system.localization.form.description")}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t("pro.system.localization.form.descriptionPlaceholder")}
          disabled={saving}
        />

        <div>
          <span className={formStyles.label}>
            {t("pro.system.localization.form.flag")}
          </span>
          {token ? (
            <MediaUploader
              mediaType="image"
              items={flagItems}
              onChange={() => {}}
              onApply={handleFlagApply}
              onRemove={handleFlagRemove}
              uploadFile={(file) => localizationApi.uploadLanguageFlag(token, file)}
              maxItems={1}
              shape="rectangle"
              placeholder={t("pro.system.localization.form.flagPlaceholder")}
              error={error}
              onError={setError}
            />
          ) : (
            <Callout variant="error">
              {t("pro.system.localization.form.flagSignIn")}
            </Callout>
          )}
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
          <input
            type="checkbox"
            checked={isEnglish ? true : enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            disabled={saving || isEnglish || (isEdit && existing?.isDefault)}
          />
          {t("pro.system.localization.form.enabled")}
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 14 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => {
                const next = e.target.checked;
                setIsDefault(next);
                if (next) setEnabled(true);
              }}
              disabled={saving || (isEdit && existing?.isDefault)}
            />
            {t("pro.system.localization.form.isDefault")}
          </span>
          <span style={{ color: vars.color.fgMuted, fontSize: 12, paddingLeft: 24 }}>
            {t("pro.system.localization.form.isDefaultHint")}
          </span>
        </label>

        {isEdit && existing ? (
          <Link
            to="/system/localization/definitions"
            search={{ lang: existing.code }}
            onClick={() => setOpen(false)}
            style={{
              alignSelf: "flex-start",
              marginTop: 4,
              fontSize: 13,
              color: vars.color.fgMuted,
              textDecoration: "underline",
              textUnderlineOffset: 2,
            }}
          >
            {t("pro.system.localization.form.editDefinitionsLink", { name: existing.name })}
          </Link>
        ) : null}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="button"
          variant="ghost"
          color="neutral"
          onClick={() => setOpen(false)}
          disabled={saving}
        >
          {t("shared.common.cancel")}
        </Button>
        <Button
          type="button"
          color="primary"
          onClick={() => void handleSave()}
          disabled={saving || !name.trim() || (!isEdit && !code.trim())}
        >
          {saving
            ? t("pro.system.localization.form.saving")
            : t("shared.common.save")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
